import type { ReactNode } from "react"
import { IconFileContentOutline18 as DocumentIcon } from "nucleo-ui-outline-18"

import type { LinearMention } from "@productlane/lib/src/linear-mentions"
import { Badge, IssueStatusIcon } from "@productlane/ui"

import type { RouterOutputs } from "@/lib/api"
import { getProjectIcon } from "@/lib/projects"

type ResolvedLinearMentions = RouterOutputs["linear"]["mentions"]

const ICON_CLASS = "h-3.5 w-3.5 flex-none"

interface Props {
	mention: LinearMention
	resolved: ResolvedLinearMentions | undefined
}

function IconSlot({ children }: { children?: ReactNode }) {
	return children ?? <span aria-hidden className={ICON_CLASS} />
}

export function LinearMentionBadge({ mention, resolved }: Props) {
	return (
		<Badge
			variant="mention"
			size="inline"
			className="hover:bg-gray-200 dark:hover:bg-gray-700"
		>
			<MentionContent mention={mention} resolved={resolved} />
		</Badge>
	)
}

function MentionContent({ mention, resolved }: Props) {
	switch (mention.kind) {
		case "issue": {
			const issue = resolved?.issues.find(
				(candidate) => candidate.identifier === mention.identifier,
			)
			return (
				<>
					<IconSlot>
						{issue?.stateType ? (
							<IssueStatusIcon
								status={issue.stateType}
								color={issue.stateColor ?? undefined}
								className={ICON_CLASS}
							/>
						) : null}
					</IconSlot>
					<span className="flex-none text-secondary">{mention.identifier}</span>
					{issue ? <span className="truncate">{issue.title}</span> : null}
				</>
			)
		}
		case "project": {
			const project = resolved?.projects.find(
				(candidate) => candidate.slugId === mention.slugId,
			)
			const Icon = getProjectIcon(project?.icon)
			return (
				<>
					<Icon
						className={ICON_CLASS}
						style={{ color: project?.color ?? undefined }}
					/>
					<span className="truncate">
						{mention.label ?? project?.name ?? "Project"}
					</span>
				</>
			)
		}
		case "document": {
			const document = resolved?.documents.find(
				(candidate) => candidate.slugId === mention.slugId,
			)
			const Icon = document?.icon ? getProjectIcon(document.icon) : DocumentIcon
			return (
				<>
					<Icon
						className={ICON_CLASS}
						style={{ color: document?.color ?? undefined }}
					/>
					<span className="truncate">
						{mention.label ?? document?.title ?? "Document"}
					</span>
				</>
			)
		}
		case "user":
			return <span className="truncate">@{mention.username}</span>
	}
}
